import * as Sentry from '@sentry/react'

// Export logger for use throughout the application
export const { logger } = Sentry

function getClickSpanName(el: HTMLElement): string {
  const ariaLabel = el.getAttribute('aria-label')
  if (ariaLabel?.trim()) return ariaLabel.trim()
  const labelledById = el.getAttribute('aria-labelledby')
  if (labelledById) {
    const labelEl = document.getElementById(labelledById)
    const label = labelEl?.textContent?.trim()?.slice(0, 50)
    if (label) return label
  }
  const text = el.textContent?.trim()?.slice(0, 50)
  if (text) return text
  const tag = el.tagName.toLowerCase()
  const role = el.getAttribute('role') || ''
  const type = (el as HTMLInputElement).type || ''
  return [tag, role, type].filter(Boolean).join(' ') || 'click'
}

function registerClickInstrumentation(): void {
  if (typeof document === 'undefined') return
  document.addEventListener(
    'click',
    (e) => {
      const target = (e.target as HTMLElement).closest(
        'a, button, [role="button"], [role="menuitem"], input[type="submit"], input[type="button"]'
      ) as HTMLElement | null
      if (!target) return
      const name = getClickSpanName(target)
      Sentry.startSpan(
        {
          op: 'ui.click',
          name: name || 'click',
          attributes: {
            'ui.element': target.tagName.toLowerCase(),
            'ui.role': target.getAttribute('role') || '',
          },
        },
        () => {}
      )
    },
    true
  )
}
/**
 * Initialize Sentry for error tracking, performance monitoring, and session replay.
 * This configuration should be called as early as possible in the application lifecycle.
 */
export function initializeSentry(): void {
  const dsn = import.meta.env['VITE_PUBLIC_SENTRY_DSN']
  const environment = import.meta.env['VITE_SENTRY_ENVIRONMENT'] || 'development'

  // Optional: override trace sample rate via env (e.g. VITE_SENTRY_TRACES_SAMPLE_RATE=0.5 for 50% in prod)
  const envTracesRate = import.meta.env['VITE_SENTRY_TRACES_SAMPLE_RATE']
  const parsed = envTracesRate !== undefined && envTracesRate !== '' ? Number(envTracesRate) : NaN
  const tracesSampleRate =
    !Number.isNaN(parsed) && parsed >= 0 && parsed <= 1 ? parsed : environment === 'production' ? 0.1 : 1.0

  // Optional: override replay session sample rate via env (e.g. VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE=0.2)
  const envReplaysRate = import.meta.env['VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE']
  const parsedReplays = envReplaysRate !== undefined && envReplaysRate !== '' ? Number(envReplaysRate) : NaN
  const replaysSessionSampleRate =
    !Number.isNaN(parsedReplays) && parsedReplays >= 0 && parsedReplays <= 1
      ? parsedReplays
      : environment === 'production'
        ? 0.1
        : 1.0

  // Skip initialization if DSN is not provided
  if (!dsn) {
    console.warn('Sentry DSN not configured. Skipping Sentry initialization.')
    return
  }

  Sentry.init({
    dsn,
    environment,

    // Enable logs to be sent to Sentry
    enableLogs: true,

    integrations: [
      // Automatic browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),

      // Session Replay to capture user interactions
      Sentry.replayIntegration({
        // Sample 10% of normal sessions
        maskAllText: true,
        blockAllMedia: true,
      }),

      // Send console.warn and console.error calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],

    // Configure trace propagation for performance monitoring
    tracePropagationTargets: ['localhost', /^https:\/\/.*\.inflectionmedicine\.com$/, /^\/api\//],

    // Performance Monitoring (configurable via VITE_SENTRY_TRACES_SAMPLE_RATE; default 10% prod, 100% dev)
    tracesSampleRate,

    // Session Replay sample rates (replaysSessionSampleRate configurable via VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE)
    replaysSessionSampleRate,
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Send default PII (Personally Identifiable Information) to Sentry
    // Set to false if you want to avoid sending PII
    sendDefaultPii: false,

    // Before sending events, allow filtering or modification
    beforeSend(event) {
      // Filter out specific errors if needed
      // For example, ignore network errors from certain domains
      return event
    },

    // Before sending breadcrumbs, allow filtering or modification
    beforeBreadcrumb(breadcrumb) {
      // Filter or modify breadcrumbs if needed
      return breadcrumb
    },
  })

  registerClickInstrumentation()
}
